import type { AdminOutput } from '../../api'
import { TRACE_STAGE, type RelayTraceSample } from './api'
import { clock, ms1, seq } from './format'
import { PlatSlot, platformOf } from './ui'

/*
 * One sampled frame on its way (GET /routes/{id}/traces; design #r0 (b)):
 * ring write and socket send on the ingress, receive and bus write on the
 * egress, then the platform socket writes. Offsets from the ring write; steps
 * between nodes are trusted only when both clocks are (|offset| ≤ 20 ms):
 * otherwise drawn amber as "clock suspect".
 */

export function TraceSample({ tr, outputs, nameOf = (id) => id }: { tr: RelayTraceSample; outputs: AdminOutput[]; nameOf?: (id: string) => string }) {
  const stages = [...tr.stages].sort((a, b) => a.unix_ns - b.unix_ns)
  const t0 = stages[0]?.unix_ns ?? 0
  const off = (ns: number) => (ns - t0) / 1e6
  const outs = [...(tr.outputs ?? [])].sort((a, b) => a.unix_ns - b.unix_ns)
  const end = Math.max(...stages.map((s) => off(s.unix_ns)), ...outs.map((o) => off(o.unix_ns)), 1)
  const max = end * 1.08
  const X = (t: number) => 30 + (t / max) * 880
  const send = stages.find((s) => s.stage === 2)
  const recv = stages.find((s) => s.stage === 3)
  const hop = send && recv ? off(recv.unix_ns) - off(send.unix_ns) : undefined
  const suspect = tr.clock_suspect
  const out = (id: string) => outputs.find((o) => o.output_id === id)
  const tick = max > 200 ? 100 : max > 80 ? 20 : max > 40 ? 10 : 5
  const lastBus = [...stages].reverse().find((s) => s.stage === 4)
  return (
    <div data-testid="trace-sample" data-suspect={suspect ? 'true' : undefined}>
      <div className="mb-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-muted">
        <span>
          Frame <span className="num text-text-soft">seq {seq(tr.seq)}</span> · epoch e{tr.epoch} · {tr.reason} · <span className="num">{clock(tr.t)}</span>
        </span>
        {suspect ? (
          <span className="text-warn">▲ clock suspect: offsets between nodes are not trusted</span>
        ) : (
          <span className="text-live">✓ clocks trusted</span>
        )}
      </div>
      <div className="overflow-x-auto">
        <svg viewBox="0 0 940 92" width="100%" height={92} role="img" aria-label={`Trace of frame ${tr.seq}: ${stages.map((s) => `${TRACE_STAGE[s.stage] ?? s.stage} ${ms1(off(s.unix_ns))}`).join(', ')}`} className="block min-w-[560px]">
          <line x1={X(0)} x2={X(max)} y1={46} y2={46} className="stroke-line-2" />
          {send && recv && hop !== undefined && (
            <>
              <rect
                x={X(off(send.unix_ns))}
                y={40}
                width={Math.max(2, X(off(recv.unix_ns)) - X(off(send.unix_ns)))}
                height={12}
                rx={2}
                className={suspect ? 'fill-warn/[.08] stroke-warn/60' : 'fill-live/[.08] stroke-live/50'}
                strokeDasharray={suspect ? '4 3' : undefined}
              />
              <text x={(X(off(send.unix_ns)) + X(off(recv.unix_ns))) / 2} y={36} textAnchor="middle" className={`${suspect ? 'fill-warn' : 'fill-text-soft'} font-mono`} fontSize="10">
                hop {nameOf(send.node)} → {nameOf(recv.node)} · {ms1(hop)}
                {suspect ? ' · suspect' : ''}
              </text>
            </>
          )}
          {stages.map((s, i) => (
            <g key={`${s.node}-${s.stage}-${i}`} transform={`translate(${X(off(s.unix_ns))} 0)`}>
              <line x1={0} x2={0} y1={40} y2={52} className="stroke-text" />
              <circle cy={i % 2 ? 76 : 18} r={8} className="fill-panel stroke-text" />
              <text y={i % 2 ? 79.5 : 21.5} textAnchor="middle" className="fill-text font-mono" fontSize="9.5">
                {s.stage}
              </text>
            </g>
          ))}
          {outs.map((o) => (
            <line key={o.output_id} x1={X(off(o.unix_ns))} x2={X(off(o.unix_ns))} y1={42} y2={50} className="stroke-accent" strokeWidth="1.5" />
          ))}
          {Array.from({ length: Math.floor(max / tick) + 1 }, (_, k) => k * tick).map((t) => (
            <text key={t} x={X(t)} y={90} textAnchor="middle" className="fill-muted font-mono" fontSize="9.5">
              {t} ms
            </text>
          ))}
        </svg>
      </div>
      <div className="overflow-x-auto">
        <table className="net-table mt-1 min-w-[520px]">
          <thead>
            <tr>
              <th scope="col">#</th>
              <th scope="col">Stage</th>
              <th scope="col">Node</th>
              <th scope="col" className="text-right">Offset</th>
              <th scope="col" className="text-right">Step</th>
            </tr>
          </thead>
          <tbody>
            {stages.map((s, i) => {
              const prev = stages[i - 1]
              const cross = !!prev && prev.node !== s.node
              return (
                <tr key={`${s.node}-${s.stage}-${i}`}>
                  <td className="num text-xs">{s.stage}</td>
                  <td className="text-xs">{TRACE_STAGE[s.stage] ?? `stage ${s.stage}`}</td>
                  <td className="font-mono text-xs text-text-soft">{nameOf(s.node)}</td>
                  <td className="num text-right text-xs">+{ms1(off(s.unix_ns))}</td>
                  <td className={`num text-right text-xs ${cross && suspect ? 'text-warn' : 'text-muted'}`}>
                    {prev ? `+${ms1(off(s.unix_ns) - off(prev.unix_ns))}` : '—'}
                    {cross && suspect ? ' ▲' : ''}
                  </td>
                </tr>
              )
            })}
            {outs.map((o, i) => {
              const ao = out(o.output_id)
              return (
                <tr key={o.output_id}>
                  <td className="num text-xs">5{String.fromCharCode(97 + i)}</td>
                  <td className="text-xs">
                    <span className="inline-flex items-center gap-1.5">
                      {ao && <PlatSlot p={platformOf(ao.profile)} size={18} />} platform write · {ao?.name ?? o.output_id}
                    </span>
                  </td>
                  <td className="font-mono text-xs text-text-soft">{lastBus ? nameOf(lastBus.node) : '—'}</td>
                  <td className="num text-right text-xs">+{ms1(off(o.unix_ns))}</td>
                  <td className="num text-right text-xs text-muted">{lastBus ? `+${ms1(off(o.unix_ns) - off(lastBus.unix_ns))}` : '—'}</td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
    </div>
  )
}
